import type { StatusTone } from '@beonit/adaptive-raindrop';
import {
  type AnalysisRun,
  type GroupBatch,
  type GroupBatchCancellation,
  type GroupBatchDeletion,
  type GroupBatchPause,
  type GroupBatchResume,
  type GroupWaitingExecution,
  isRunFinished,
  type RunStatus,
} from '../model.js';
import { batchesNewestFirst } from './latest-execution.js';

/**
 * What the «Ejecuciones» tab of a group offers on each execution, and what it says about it.
 *
 * The product owner's rules (2026-09-25):
 *
 * - a **queued** execution — runs waiting, none running — can be taken out of the queue: every
 *   run of it that has not started leaves it;
 * - a **running** one can be cancelled: the waiting runs leave the queue, the running ones are asked
 *   to stop, and what they had half-written is discarded;
 * - a **finished** one is read. Nothing finished is ever cancelled, and a cancelled execution stays
 *   in the history, marked as such. It is not a measurement: upstream writes no automatic snapshot
 *   for it, and «the latest execution» skips it (`latestBatch`).
 *
 * Both actions are the same call upstream (`POST /analysis-groups/:id/batches/cancel`); they are
 * two buttons because «Quitar de la cola» and «Cancelar» are different decisions for whoever
 * presses them, and never offered together.
 *
 * An execution launched while the group was busy **waits its turn** (product owner, 2026-09-26)
 * with no runs: it is not in the history, it goes above it, next first, and the only thing it
 * offers is «Quitar de la cola» — the same call, which takes it out without a trace.
 *
 * And one execution can be stopped without stopping the rest (product owner, 2026-09-29), which
 * the global pause of the queue could not do:
 *
 * - a started execution with runs waiting can be **paused**: those runs wait, `PAUSED`, the ones
 *   already running finish, and the group's next execution starts — a paused one never holds it;
 * - a **paused** one can be resumed — back to the end of the group's queue of executions, where it
 *   **waits its turn** again — or cancelled;
 * - one with nothing of it in flight can be **deleted**, with its results and its figures, by
 *   whoever may delete in the module (`virtual-profiler:delete`).
 */

export type ExecutionState =
  'waiting' | 'queued' | 'running' | 'paused' | 'cancelling' | 'finished' | 'cancelled';

export type ExecutionAction = 'pause' | 'resume' | 'withdraw' | 'cancel' | 'view' | 'delete';

type Counts = Partial<Record<RunStatus, number>>;

/**
 * How many runs of the execution are in these states.
 *
 * The attempts and the videos are both read and the larger taken: an action touches runs, so the
 * attempts are the truth, but an API that only sends the videos must not read as idle.
 */
function counted(
  batch: Pick<GroupBatch, 'runsByStatus' | 'membersByStatus'>,
  statuses: readonly RunStatus[],
): number {
  const sum = (counts: Counts | null | undefined): number =>
    statuses.reduce((total, status) => total + (counts?.[status] ?? 0), 0);

  return Math.max(sum(batch.runsByStatus), sum(batch.membersByStatus));
}

const WAITING: readonly RunStatus[] = ['QUEUED', 'DISPATCHED'];
const ACTIVE: readonly RunStatus[] = ['RUNNING', 'CANCEL_REQUESTED'];
const PAUSED: readonly RunStatus[] = ['PAUSED'];

/**
 * Where an execution stands.
 *
 * Upstream's `complete` is «every run of it is final», so an execution it calls incomplete is
 * still going even when the counts this side has say otherwise. A run asked to stop by hand is not
 * the execution being cancelled: only `cancelled` — set when the whole execution was — makes it
 * `cancelling`, then `cancelled` once nothing of it moves.
 *
 * `paused` is upstream's word (`queueState`), and it holds while the runs that had already started
 * finish. An execution with paused runs and nothing else is `waiting` once upstream says it was
 * resumed and waits its turn; from an API that does not say, it is still paused.
 */
export function executionState(
  batch: Pick<
    GroupBatch,
    'runsByStatus' | 'membersByStatus' | 'complete' | 'cancelled' | 'queueState'
  >,
): ExecutionState {
  const waiting = counted(batch, WAITING);
  const active = counted(batch, ACTIVE);
  const paused = counted(batch, PAUSED);
  const moving = waiting + active + paused > 0 || !batch.complete;

  if (batch.cancelled) return moving ? 'cancelling' : 'cancelled';
  if (batch.queueState === 'paused') return 'paused';
  if (!moving) return 'finished';
  if (active > 0) return 'running';
  if (waiting > 0) return 'queued';
  if (paused > 0) {
    return batch.queueState === 'waiting' || batch.queueState === 'starting' ? 'waiting' : 'paused';
  }

  return 'running';
}

/**
 * Whether the execution already left its automatic measurement: upstream writes it once, when
 * every run of it first reached a final state, so a measured execution had finished.
 */
export function executionMeasured(batch: Pick<GroupBatch, 'snapshotId'>): boolean {
  return batch.snapshotId !== null;
}

/**
 * Whether it can be paused: started, upstream's word, with runs waiting in the queue — which is what
 * a pause stops. One whose runs are still being created, or older than the record of executions
 * (no `queueState`), upstream refuses.
 */
export function executionPausable(
  batch: Pick<GroupBatch, 'runsByStatus' | 'membersByStatus' | 'queueState'>,
): boolean {
  return batch.queueState === 'started' && counted(batch, ['QUEUED']) > 0;
}

/**
 * Whether nothing of it is in flight — queued, sent, running or stopping — which is when it can be
 * deleted. Its paused runs do not count: they are deleted with it.
 */
export function executionSettled(
  batch: Pick<GroupBatch, 'runsByStatus' | 'membersByStatus'>,
): boolean {
  return counted(batch, [...WAITING, ...ACTIVE]) === 0;
}

/**
 * What an execution offers, in the order the buttons go.
 *
 * Never `withdraw` and `cancel` together, never either on something finished, and nothing while a
 * cancellation lands — offering it again would read as a button that did nothing. «Ver» on every
 * execution whose runs are all final, a cancelled one included: it stays in the history, it is
 * only not a measurement.
 *
 * Nor either on an execution that was already `measured`: it had finished, and what moves in it
 * now reached it afterwards — a «Repetir», or an automatic retry the sweep queued after its end.
 * Cancelling the whole execution for it would mark a finished measurement as cancelled; that run
 * is withdrawn or cancelled on its own, from the execution's row. The same goes for pausing it.
 *
 * `pause` only while it has runs waiting (`pausable`): with every run of it already running there
 * is nothing left to hold. A paused one offers `resume` and `cancel`; one resumed and waiting its
 * turn, `withdraw`. `delete` needs `canDelete` and nothing of it in flight (`settled`) — always so
 * once it finished or was cancelled.
 */
export function executionActions(
  state: ExecutionState,
  options: {
    canOperate: boolean;
    canDelete?: boolean;
    measured?: boolean;
    pausable?: boolean;
    settled?: boolean;
  },
): ExecutionAction[] {
  const mayStop = options.canOperate && options.measured !== true;
  const pause: ExecutionAction[] = mayStop && options.pausable === true ? ['pause'] : [];
  const remove = (settled: boolean): ExecutionAction[] =>
    options.canDelete === true && settled ? ['delete'] : [];

  switch (state) {
    case 'queued':
      return mayStop ? [...pause, 'withdraw'] : [];
    case 'running':
      return mayStop ? [...pause, 'cancel'] : [];
    case 'paused':
      return [
        ...(options.canOperate ? (['resume', 'cancel'] as const) : []),
        ...remove(options.settled === true),
      ];
    case 'waiting':
      return [
        ...(options.canOperate ? (['withdraw'] as const) : []),
        ...remove(options.settled === true),
      ];
    case 'finished':
    case 'cancelled':
      return ['view', ...remove(true)];
    default:
      return [];
  }
}

/**
 * The one button an execution's row shows, in the order that matters most: taking it out of the
 * queue or cancelling it before looking at it. Null when it offers nothing. Everything else the
 * row can do — «Comparar con la anterior» — goes behind its «⋯», so the column stays narrow
 * enough for the table to fit without a horizontal scroll.
 */
export function primaryExecutionAction(
  actions: readonly ExecutionAction[],
): ExecutionAction | null {
  return (
    (['withdraw', 'cancel', 'view'] as const).find((action) => actions.includes(action)) ?? null
  );
}

/**
 * What each action's button says, short, and what its title and accessible name say in full:
 * «Quitar de la cola» does not fit the column, «Quitar» on a queued execution does.
 */
export const EXECUTION_ACTION_LABELS: Readonly<
  Record<ExecutionAction, { label: string; title: string }>
> = Object.freeze({
  pause: { label: 'Pausar', title: 'Pausar la ejecución' },
  resume: { label: 'Reanudar', title: 'Reanudar la ejecución' },
  withdraw: { label: 'Quitar', title: 'Quitar de la cola' },
  cancel: { label: 'Cancelar', title: 'Cancelar la ejecución' },
  view: { label: 'Ver', title: 'Ver sus cifras y sus vídeos' },
  delete: { label: 'Borrar', title: 'Borrar la ejecución y sus resultados' },
});

/** The counts of an execution, for its compact progress line. */
export interface ExecutionProgress {
  /** Waiting, or sent to a worker and not started. */
  queued: number;
  /** Running, or asked to stop and not stopped yet. */
  running: number;
  /** Not started, and held while the execution is paused. */
  paused: number;
  succeeded: number;
  failed: number;
  cancelled: number;
  total: number;
  /** Counted by video, each by its latest run there; by attempt from an API that does not. */
  byVideo: boolean;
}

/**
 * The counts of an execution.
 *
 * By video when upstream counts them — the way that execution's metrics count it, so a failure its
 * automatic retry healed does not read as an error — and by attempt only from an API that does not.
 */
export function executionProgress(
  batch: Pick<GroupBatch, 'runs' | 'runsByStatus' | 'members' | 'membersByStatus'>,
): ExecutionProgress {
  const byVideo = batch.members !== null && batch.membersByStatus !== null;
  const counts: Counts = (byVideo ? batch.membersByStatus : batch.runsByStatus) ?? {};
  const of = (statuses: readonly RunStatus[]): number =>
    statuses.reduce((total, status) => total + (counts[status] ?? 0), 0);

  return {
    queued: of(WAITING),
    running: of(ACTIVE),
    paused: of(PAUSED),
    succeeded: of(['SUCCEEDED']),
    failed: of(['FAILED']),
    cancelled: of(['CANCELLED']),
    total: byVideo ? (batch.members ?? 0) : batch.runs,
    byVideo,
  };
}

/**
 * «7/10 · 6 ok · 1 con error · 1 en marcha · 2 en cola»: how many finished out of how many, then
 * only the counts that are not zero — «3 en pausa» last. A cancelled one is named by what was
 * counted — «cancelados» are videos, «canceladas» attempts.
 */
export function executionProgressLine(progress: ExecutionProgress): string {
  const done = progress.succeeded + progress.failed + progress.cancelled;
  const cancelledWord = `cancelad${progress.byVideo ? 'o' : 'a'}${progress.cancelled === 1 ? '' : 's'}`;
  const parts = [
    progress.succeeded > 0 ? `${progress.succeeded} ok` : null,
    progress.failed > 0 ? `${progress.failed} con error` : null,
    progress.cancelled > 0 ? `${progress.cancelled} ${cancelledWord}` : null,
    progress.running > 0 ? `${progress.running} en marcha` : null,
    progress.queued > 0 ? `${progress.queued} en cola` : null,
    progress.paused > 0 ? `${progress.paused} en pausa` : null,
  ].filter((part): part is string => part !== null);

  return [`${done}/${progress.total}`, ...parts].join(' · ');
}

/** The badge beside an execution's label. */
export function executionBadge(
  state: ExecutionState,
  progress: Pick<ExecutionProgress, 'failed'>,
): { label: string; tone: StatusTone } {
  switch (state) {
    case 'waiting':
      return { label: 'En espera', tone: 'neutral' };
    case 'queued':
      return { label: 'En cola', tone: 'neutral' };
    case 'running':
      return { label: 'En marcha', tone: 'info' };
    case 'paused':
      return { label: 'En pausa', tone: 'neutral' };
    case 'cancelling':
      return { label: 'Cancelando', tone: 'warning' };
    case 'cancelled':
      return { label: 'Cancelada', tone: 'neutral' };
    default:
      return progress.failed > 0
        ? { label: 'Terminada con errores', tone: 'warning' }
        : { label: 'Terminada', tone: 'success' };
  }
}

export interface ExecutionConfirmation {
  title: string;
  description: string;
  actionLabel: string;
}

/** The actions on an execution that ask first: every one but «Ver». */
export type ConfirmedExecutionAction = Exclude<ExecutionAction, 'view'>;

/**
 * What is asked before acting on an execution: one short sentence each.
 *
 * Withdrawing and cancelling say the execution stops counting as a measurement: upstream marks it
 * cancelled as soon as one run of it is withdrawn, so emptying the queue of an execution that is
 * mostly done — a last automatic retry waiting — loses its numbers too. Pausing says what goes on,
 * and that the group does not wait for it; deleting, that it cannot be undone.
 */
export function executionConfirmation(
  action: ConfirmedExecutionAction,
  label: string,
): ExecutionConfirmation {
  switch (action) {
    case 'pause':
      return {
        title: `¿Pausar «${label}»?`,
        description:
          'Sus análisis pendientes esperan hasta que la reanudes; los que están en marcha terminan y la siguiente ejecución del grupo puede empezar.',
        actionLabel: 'Pausar',
      };
    case 'resume':
      return {
        title: `¿Reanudar «${label}»?`,
        description:
          'Vuelve a la cola de ejecuciones del grupo y sus análisis pendientes salen cuando le toque.',
        actionLabel: 'Reanudar',
      };
    case 'withdraw':
      return {
        title: `¿Quitar «${label}» de la cola?`,
        description:
          'Sus análisis pendientes salen de la cola y la ejecución deja de contar como medición.',
        actionLabel: 'Quitar de la cola',
      };
    case 'delete':
      return {
        title: `¿Borrar «${label}»?`,
        description: 'Se borran sus resultados y sus cifras. No se puede deshacer.',
        actionLabel: 'Borrar',
      };
    default:
      return {
        title: `¿Cancelar «${label}»?`,
        description:
          'Se paran sus análisis y se descarta lo que dejen a medias; lo terminado se conserva, sin contar como medición.',
        actionLabel: 'Cancelar la ejecución',
      };
  }
}

/**
 * What the toast says once upstream answered.
 *
 * «Cancelación pedida» and never «cancelada»: a running analysis stops at its next checkpoint, and
 * one that was already saving its result finishes anyway, which the toast says rather than let it
 * be discovered. A withdrawal that caught a run starting in between reads as a cancellation,
 * because that is what it became. When nothing was touched — called twice, or the execution ended
 * by itself meanwhile — it says so instead of claiming it did something.
 */
export function cancelToast(
  result: GroupBatchCancellation,
  action: 'withdraw' | 'cancel',
): { kind: 'success' | 'info'; message: string } {
  const withdrawn = result.withdrawn.length;
  const cancelling = result.cancelling.length;
  const finishing = result.finishing.length;

  if (withdrawn + cancelling + finishing === 0) {
    return { kind: 'info', message: 'No quedaba nada pendiente en esta ejecución' };
  }

  if (action === 'withdraw' && cancelling === 0 && finishing === 0) {
    return {
      kind: 'success',
      message:
        withdrawn === 1
          ? 'Se ha quitado 1 análisis de la cola'
          : `Se han quitado ${withdrawn} análisis de la cola`,
    };
  }

  const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);
  const parts = [
    withdrawn > 0 ? `${withdrawn} fuera de la cola` : null,
    cancelling > 0 ? `${cancelling} parándose` : null,
    finishing > 0
      ? `${finishing} ${plural(finishing, 'terminará', 'terminarán')} porque ya ${plural(finishing, 'estaba', 'estaban')} guardando`
      : null,
  ].filter((part): part is string => part !== null);

  return { kind: 'success', message: `Cancelación pedida: ${parts.join(', ')}` };
}

/**
 * What the toast says once a pause was answered. The runs that had already started are said, since
 * they go on: somebody watching the queue would otherwise take them for a pause that did not hold.
 */
export function pauseToast(result: GroupBatchPause): {
  kind: 'success' | 'info';
  message: string;
} {
  const { paused, stillRunning } = result;

  if (paused + stillRunning === 0) return { kind: 'info', message: 'No quedaba nada que pausar' };

  const parts = [
    paused > 0 ? `${paused} en pausa` : null,
    stillRunning > 0
      ? `${stillRunning} ya en marcha ${stillRunning === 1 ? 'termina' : 'terminan'}`
      : null,
  ].filter((part): part is string => part !== null);

  return { kind: 'success', message: `Ejecución en pausa: ${parts.join(', ')}` };
}

/** What the toast says once a resume was answered: whether it went on, or waits its turn. */
export function resumeToast(result: GroupBatchResume): { kind: 'success'; message: string } {
  return {
    kind: 'success',
    message:
      result.state === 'waiting'
        ? 'Ejecución reanudada: espera su turno tras la que está en marcha'
        : 'Ejecución reanudada: sus análisis vuelven a la cola',
  };
}

/** What the toast says once a deletion was answered, with how many analyses went with it. */
export function deleteToast(result: GroupBatchDeletion): { kind: 'success'; message: string } {
  const runs = result.deletedRuns;

  if (runs === 0) return { kind: 'success', message: 'Ejecución borrada' };

  return {
    kind: 'success',
    message:
      runs === 1
        ? 'Ejecución borrada con su análisis'
        : `Ejecución borrada con sus ${runs} análisis`,
  };
}

/**
 * Which actions a run in an execution's row offers.
 *
 * - `queue`: it is still moving — withdraw or cancel that one run (`RunActions`);
 * - `ledger`: it finished in an execution that was not cancelled — repeat, promote or pin it
 *   (`RunLedgerActions`), which is how a video that fell out of an execution counts in it again;
 * - `none`: it finished in a cancelled execution. Repeating it there would bring back what was
 *   just stopped, into something that is not a measurement.
 */
export function executionRunActions(
  state: ExecutionState,
  run: Pick<AnalysisRun, 'status'>,
): 'queue' | 'ledger' | 'none' {
  if (!isRunFinished(run.status)) return 'queue';
  if (state === 'cancelled' || state === 'cancelling') return 'none';

  return 'ledger';
}

/**
 * The finished execution before this one, for «Comparar con la anterior».
 *
 * Only for a finished execution, and only against another finished one: a cancelled execution is
 * not a measurement, and one still running has no numbers yet. Ordered by when they started, not
 * by where they sit in the list.
 */
export function previousFinished(
  batches: readonly GroupBatch[] | null | undefined,
  batch: GroupBatch,
): GroupBatch | null {
  if (executionState(batch) !== 'finished') return null;

  const ordered = batchesNewestFirst(batches);
  const index = ordered.findIndex((candidate) => candidate.batch === batch.batch);
  if (index < 0) return null;

  return ordered.slice(index + 1).find((older) => executionState(older) === 'finished') ?? null;
}

const MOMENT = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * «2026-09-27 16:37» for «prueba-total-1@2026-09-27 16:37»: the group's name is on every label and
 * beside it on screen already. The whole label stays in the title, the search and the export.
 */
export function shortExecutionLabel(label: string): string {
  const at = label.lastIndexOf('@');

  return at > 0 && at < label.length - 1 ? label.slice(at + 1) : label;
}

/** When an execution ran: its start, compact, and start → end in the title once it finished. */
export function executionWhen(batch: Pick<GroupBatch, 'startedAt' | 'finishedAt'>): {
  text: string;
  title: string | undefined;
} {
  if (!batch.startedAt) return { text: '—', title: undefined };

  const started = batch.startedAt.toLocaleString('es-ES');

  return {
    text: MOMENT.format(batch.startedAt),
    title: batch.finishedAt ? `${started} → ${batch.finishedAt.toLocaleString('es-ES')}` : started,
  };
}

/** What an execution under way offers without opening its group: never «Ver» nor «Borrar». */
export type UnderWayAction = Extract<ExecutionAction, 'pause' | 'resume' | 'withdraw' | 'cancel'>;

/** An execution under way, with what it offers without opening its group. */
export interface ExecutionUnderWay {
  batch: GroupBatch;
  state: 'queued' | 'running' | 'paused';
  /** Pause or resume, then withdraw or cancel. */
  actions: UnderWayAction[];
}

/**
 * The executions of a group that are under way — queued, running or paused — newest first: what
 * the «En marcha» band shows of a group, each with its pause or resume and its cancel in view
 * rather than behind a menu (product owner, 2026-09-29).
 */
export function executionsUnderWay(
  batches: readonly GroupBatch[] | null | undefined,
  options: { canOperate: boolean },
): ExecutionUnderWay[] {
  return batchesNewestFirst(batches).flatMap((batch): ExecutionUnderWay[] => {
    const state = executionState(batch);
    if (state !== 'queued' && state !== 'running' && state !== 'paused') return [];

    const actions = executionActions(state, {
      canOperate: options.canOperate,
      measured: executionMeasured(batch),
      pausable: executionPausable(batch),
    }).filter((action): action is UnderWayAction => action !== 'view' && action !== 'delete');

    return [{ batch, state, actions }];
  });
}

// ── Executions waiting their turn ────────────────────────────────────────────

/** The group's waiting executions, the next to start first, whatever order they came in. */
export function waitingNextFirst(
  waiting: readonly GroupWaitingExecution[] | null | undefined,
): GroupWaitingExecution[] {
  return [...(waiting ?? [])].sort((a, b) => a.queuePosition - b.queuePosition);
}

/** «En espera»; «Empezando» for the moment its runs are being created. */
export function waitingExecutionBadge(state: GroupWaitingExecution['state']): {
  label: string;
  tone: StatusTone;
} {
  return state === 'starting'
    ? { label: 'Empezando', tone: 'info' }
    : { label: 'En espera', tone: 'neutral' };
}

/** «#2 en cola · Empieza cuando termine la anterior»; nothing for one already starting. */
export function waitingExecutionLine(
  execution: Pick<GroupWaitingExecution, 'state' | 'queuePosition'>,
): string {
  if (execution.state === 'starting') return '';

  return `#${execution.queuePosition} en cola · Empieza cuando termine la anterior`;
}

/** When it was launched — not when it will start, which nobody knows yet. */
export function waitingExecutionWhen(execution: Pick<GroupWaitingExecution, 'queuedAt'>): {
  text: string;
  title: string;
} {
  return {
    text: MOMENT.format(execution.queuedAt),
    title: `Lanzada el ${execution.queuedAt.toLocaleString('es-ES')}`,
  };
}

/**
 * Only «Quitar de la cola», and only while it waits: once it is starting its runs are being
 * created, and it is cancelled from the history like any other execution when it gets there.
 */
export function waitingExecutionActions(
  execution: Pick<GroupWaitingExecution, 'state'>,
  options: { canOperate: boolean },
): ExecutionAction[] {
  return options.canOperate && execution.state === 'waiting' ? ['withdraw'] : [];
}

/** What is asked before taking it out: it never ran, so nothing stops counting as a measurement. */
export function dequeueConfirmation(label: string): ExecutionConfirmation {
  return {
    title: `¿Quitar «${label}» de la cola?`,
    description: 'Todavía no ha empezado: no se ha analizado nada.',
    actionLabel: 'Quitar de la cola',
  };
}

/**
 * What the toast says once upstream answered. If it had started between the click and the answer,
 * the call withdrew its runs like any cancellation, and that is what is said.
 */
export function dequeueToast(result: GroupBatchCancellation): {
  kind: 'success' | 'info';
  message: string;
} {
  if (result.dequeued) return { kind: 'success', message: 'Se ha quitado de la cola' };

  return cancelToast(result, 'withdraw');
}
